import { getStoredToken } from "../auth/AuthContext";

export type ApiErrorBody = {
  error?: {
    code?: string;
    message?: string;
    details?: Record<string, unknown>;
  };
  timestamp?: string;
  path?: string;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, unknown>;
  raw?: ApiErrorBody;

  constructor(message: string, status: number, body?: ApiErrorBody) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.error?.code;
    this.details = body?.error?.details;
    this.raw = body;
  }
}

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
  authenticated?: boolean;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
};

// Build-time olarak Vite tarafindan dolduruluyor:
//   - dev/docker-compose: bos string -> '/api' (vite proxy / nginx proxy)
//   - render/cloud: 'https://kursiyer-backend.onrender.com' -> '<base>/api'
const RAW_BASE = (import.meta as unknown as { env?: { VITE_API_BASE_URL?: string } }).env
  ?.VITE_API_BASE_URL;
const BASE = `${(RAW_BASE || "").replace(/\/$/, "")}/api`;

const AUTH_STORAGE_KEY = "kp_auth_v1";
let unauthorizedRedirectInFlight = false;

/**
 * Oturum gecersiz (token suresi doldu / kullanici deaktif) — yereldeki oturumu
 * temizle ve login'e yonlendir. Login istegi kendisi 401 dondugunde uygulanmaz;
 * form kendi hata mesajini gosterir.
 */
function handleUnauthorized(path: string): void {
  if (path === "/auth/login") return;
  if (unauthorizedRedirectInFlight) return;
  if (window.location.pathname === "/login") return;
  unauthorizedRedirectInFlight = true;
  try {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    // storage erisilemiyorsa yine de yonlendir
  }
  window.location.replace("/login?expired=1");
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = path.startsWith("/") ? `${BASE}${path}` : `${BASE}/${path}`;
  if (!query) return url;
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    search.append(key, String(value));
  });
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function apiRequest<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    "content-type": "application/json",
    ...(options.headers || {}),
  };

  if (options.authenticated !== false) {
    const token = getStoredToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }

  const res = await fetch(buildUrl(path, options.query), {
    method: options.method || "GET",
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!res.ok) {
    if (res.status === 401 && options.authenticated !== false) {
      handleUnauthorized(path);
    }
    const body = (parsed || {}) as ApiErrorBody;
    const message = body?.error?.message || `HTTP ${res.status}`;
    throw new ApiError(message, res.status, body);
  }

  return parsed as T;
}

type RawRequestOptions = {
  method?: "GET" | "POST" | "PUT";
  query?: RequestOptions["query"];
  body?: BodyInit;
  signal?: AbortSignal;
  authenticated?: boolean;
};

async function apiRaw<T = unknown>(path: string, options: RawRequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  if (options.authenticated !== false) {
    const token = getStoredToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }
  const res = await fetch(buildUrl(path, options.query), {
    method: options.method || "POST",
    headers,
    body: options.body,
    signal: options.signal,
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }
  if (!res.ok) {
    if (res.status === 401 && options.authenticated !== false) {
      handleUnauthorized(path);
    }
    const body = (parsed || {}) as ApiErrorBody;
    const message = body?.error?.message || `HTTP ${res.status}`;
    throw new ApiError(message, res.status, body);
  }
  return parsed as T;
}

async function apiBlob(path: string, options: { query?: RequestOptions["query"]; signal?: AbortSignal } = {}): Promise<Blob> {
  const headers: Record<string, string> = {};
  const token = getStoredToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(buildUrl(path, options.query), {
    method: "GET",
    headers,
    signal: options.signal,
  });
  if (!res.ok) {
    if (res.status === 401) {
      handleUnauthorized(path);
    }
    let body: ApiErrorBody = {};
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      // noop
    }
    throw new ApiError(body?.error?.message || `HTTP ${res.status}`, res.status, body);
  }
  return res.blob();
}

export const api = {
  get: <T = unknown>(path: string, query?: RequestOptions["query"], options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "GET", query }),
  post: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "POST", body }),
  put: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "PUT", body }),
  patch: <T = unknown>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "PATCH", body }),
  delete: <T = unknown>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: "DELETE" }),
  upload: <T = unknown>(path: string, form: FormData, options?: Omit<RawRequestOptions, "body" | "method">) =>
    apiRaw<T>(path, { ...options, method: "POST", body: form }),
  getBlob: (path: string, query?: RequestOptions["query"]) => apiBlob(path, { query }),
};
